import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import PortalIdParser from 'PortalIdParser';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { H2 } from 'foundations-components/transitional/components/H2';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { InstallWizard } from 'marketplace-ui-apps-core/details/components/installFlow/InstallWizard';
import { useOAuthUrl } from 'marketplace-ui-apps-core/details/hooks/useOAuthUrl';
import { UninstallableMessage } from 'marketplace-ui-apps-core/installability/components/UninstallableMessage';
import { useAppInstallability } from 'marketplace-ui-apps-core/installability/hooks/useAppInstallability';
import { Fragment, useCallback, useState } from 'react';
import { useGetInstallCompatibilityQuery } from '../../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc.hooks';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { getFormattedUrl } from '../utils/DetailsPageUrls';
import { trackCustomMarketplaceOfferingBehavioralEvent } from '../utils/TrackingUtils';
import { RequestPermissionsModal } from './RequestPermissionsModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AsyncIntegrationsConnectButton = Loadable({
  loader: () => import(
  // @ts-expect-error untyped import
  /* webpackChunkName: 'integrations-connect-button' */
  'ui-addon-integrations-directory-panel/UIIntegrationsConnectButton')
});
const AsyncIntegrationsConnectModal = Loadable({
  loader: () => import(
  // @ts-expect-error untyped import
  /* webpackChunkName: 'integrations-connect-modal' */
  'ui-addon-integrations-directory-panel/UIIntegrationsConnectModal')
});
const AsyncDataSyncConnectButton = Loadable({
  loader: () => import( /* webpackChunkName: 'data-sync-connect-button' */'ui-universal-auth/DataSyncAuthButton')
});
const AsyncDashworksAuthButton = Loadable({
  loader: () => import( /* webpackChunkName: 'dashworks-auth-button' */'ui-universal-auth/DashworksAuthButton').then(module => module.DashworksAuthButton)
});
export function AppCTAButton({
  features,
  installType,
  installUrl = '',
  isUIE,
  offeringId,
  onClick,
  onConnectionComplete,
  onInstallComplete = () => {},
  onInstallError = () => {},
  sourceId
}) {
  const {
    appContext,
    iconUrl,
    listingId,
    name,
    published,
    isEmbedded,
    trackInstall,
    trackInteraction
  } = useDetailsPageContext();
  const [standardInstallModalOpen, setStandardInstallModalOpen] = useState(false);
  const [homemadeIframeModalOpen, setHomemadeIframeModalOpen] = useState(false);
  const [openPermissionsModal, setOpenPermissionsModal] = useState(false);
  const isPreview = !published;
  const onInstallClickDefault = () => {
    if (onClick) {
      onClick();
    }
    trackInstall({});
    trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
  };

  // Do not use sourceId as a number for AGENT productType. Known issue with globalId size causing parseInt to incorrectly convert.
  const sourceIdAsNumber = sourceId ? typeof sourceId === 'number' ? sourceId : parseInt(sourceId, 10) : undefined;

  // We will eventually use installCompatibilityData for all installability logic
  const {
    loading: installCompatibilityLoading
  } = useGetInstallCompatibilityQuery({
    // Until we have the Framework listingId from UDS V3 for apps, we will skip this query
    skip: true,
    // appContext !== 'app',
    variables: {
      listingId: listingId !== null && listingId !== void 0 ? listingId : 0,
      published
    }
  });

  // LEGACY CALL: to be replaced by useGetInstallabilityQuery when it is fully functional
  // We will use uninstallableReason from here for now
  const {
    installabilityLoading,
    uninstallableReason
  } = useAppInstallability({
    appContext,
    offeringId: offeringId !== null && offeringId !== void 0 ? offeringId : 0,
    features
  });

  // Only request OAuth URL if app is STANDARD installType
  const {
    oauthUrl,
    oauthUrlLoading
  } = useOAuthUrl({
    offeringId,
    published,
    skip: appContext !== 'app' || installType !== 'STANDARD'
  });
  const handleConnectionComplete = useCallback(payload => {
    if (onConnectionComplete) {
      // Analytics TO DO
      onConnectionComplete(payload);
    } else if (payload) {
      const redirectUrl = payload && payload.meta && payload.meta.redirectUrl;
      // In marketplace-ui-details, we might want to show a success message instead of redirecting
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    }
    onInstallComplete();
  }, [onConnectionComplete, onInstallComplete]);
  const handleConnectionFailed = useCallback(err => {
    var _err$meta, _err$meta2;
    const errorType = ((_err$meta = err.meta) === null || _err$meta === void 0 ? void 0 : _err$meta.type) || 'default';
    const errorMessage = (_err$meta2 = err.meta) === null || _err$meta2 === void 0 ? void 0 : _err$meta2.appErrorMessage;

    // For INTEGRATION_SPECIFIC_ERROR, use the errorMessage directly as the message
    if (errorType === 'INTEGRATION_SPECIFIC_ERROR' && errorMessage) {
      FloatingAlertStore.addAlert({
        message: errorMessage,
        titleText: unescapedText(`detailsPage.productDetailsCard.cta.error.${errorType}.titleText`, {
          name
        }),
        type: 'danger'
      });
    } else {
      // For all other error types, use the static translations
      FloatingAlertStore.addAlert({
        message: unescapedText(`detailsPage.productDetailsCard.cta.error.${errorType}.message`, {
          name
        }),
        titleText: unescapedText(`detailsPage.productDetailsCard.cta.error.${errorType}.titleText`, {
          name
        }),
        type: 'danger'
      });
    }
    onInstallError();
  }, [name, onInstallError]);

  // Loading state
  if (installCompatibilityLoading || installabilityLoading || oauthUrlLoading) {
    return /*#__PURE__*/_jsx(LoadingButton, {
      size: "medium",
      use: "primary",
      disabled: true,
      loading: true,
      role: "button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.APP"
      })
    });
  }

  // Renders a "Request to install" button for missing user permissions
  if (!isPreview &&
  // if isPreview has reached this component, we want to make it to the STANDARD installType logic block
  uninstallableReason && uninstallableReason.label === 'MISSING_USER_PERMISSIONS') {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: () => {
          setOpenPermissionsModal(true);
          trackInteraction({
            action: 'click request install app permission button'
          });
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.request"
        })
      }), openPermissionsModal && /*#__PURE__*/_jsx(RequestPermissionsModal, {
        listingId: listingId !== null && listingId !== void 0 ? listingId : 0,
        missingHubUserRoles: uninstallableReason.missingHubUserRoles,
        onClose: () => setOpenPermissionsModal(false)
      })]
    });
  }

  // Disabled or Trial button with tooltip for all other uninstallable reasons
  if (!isPreview &&
  // if isPreview has reached this component, we want to make it to the STANDARD installType logic block
  uninstallableReason) {
    const tooltipButton = /*#__PURE__*/_jsx(Tooltip, {
      title: /*#__PURE__*/_jsx(UninstallableMessage, {
        uninstallableReason: uninstallableReason
      }),
      children: /*#__PURE__*/_jsx(Button, {
        use: "primary",
        disabled: true,
        role: "button",
        onClick: () => {
          trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
          trackInstall({});
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.install.APP"
        })
      })
    });
    return tooltipButton;
  }

  // Button that renders standard InstallWizard for apps with configured OAuth URLs
  if (installType === 'STANDARD' && oauthUrl) {
    const oauthQueryParams = oauthUrl ? new URL(oauthUrl).search : '';
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: () => {
          setStandardInstallModalOpen(true);
          trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
          trackInteraction({
            action: 'open install wizard'
          });
          trackInstall({});
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.install.APP"
        })
      }), standardInstallModalOpen && /*#__PURE__*/_jsx(InstallWizard, {
        appId: String(sourceId !== null && sourceId !== void 0 ? sourceId : ''),
        iconUrl: iconUrl,
        isPreview: isPreview,
        isUIE: isUIE,
        name: name,
        oauthQueryParams: oauthQueryParams,
        offeringId: offeringId,
        onClose: () => {
          trackInteraction({
            action: 'close install wizard'
          });
          setStandardInstallModalOpen(false);
        },
        onTrackInteraction: action => trackInteraction({
          action
        }),
        portalId: PortalIdParser.get()
      })]
    });
  }

  // Simple button with href for LEGACY and HOMEMADE_EXTERNAL installTypes
  if (installType === 'LEGACY' || installType === 'HOMEMADE_EXTERNAL') {
    return /*#__PURE__*/_jsx(Button, {
      use: "primary",
      external: installType === 'HOMEMADE_EXTERNAL' || isEmbedded,
      href: getFormattedUrl(installUrl),
      onClick: onInstallClickDefault,
      role: "button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.APP"
      })
    });
  }

  // Data sync auth button
  if (installType === 'HOMEMADE_DATA_SYNC') {
    return /*#__PURE__*/_jsx(AsyncDataSyncConnectButton, {
      portalId: PortalIdParser.get(),
      appId: sourceId !== null && sourceId !== void 0 ? sourceId : 0,
      use: "primary",
      onClick: onInstallClickDefault,
      onComplete: onInstallComplete,
      trackingSource: "marketplace",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.APP"
      })
    });
  }

  // Dashworks auth button
  if (installType === 'HOMEMADE_DASHWORKS') {
    return /*#__PURE__*/_jsx(AsyncDashworksAuthButton, {
      portalId: PortalIdParser.get(),
      appId: sourceIdAsNumber !== null && sourceIdAsNumber !== void 0 ? sourceIdAsNumber : 0,
      use: "primary",
      onClick: onInstallClickDefault,
      onComplete: __secretId => {
        trackInteraction({
          action: 'dashworks app connected'
        });
        // Refresh the page to show updated connection status
        onInstallComplete();
        window.location.reload();
      },
      onCancel: () => {
        trackInteraction({
          action: 'dashworks app connection cancelled'
        });
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.APP"
      })
    });
  }

  // Iframe modal button for builtByHubSpot modal connections
  if (installType === 'HOMEMADE_MODAL') {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: () => {
          setHomemadeIframeModalOpen(true);
          trackInstall({});
          trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
          // Add analytics event
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.install.APP"
        })
      }), homemadeIframeModalOpen && /*#__PURE__*/_jsxs(Modal, {
        width: 600,
        use: "success",
        children: [/*#__PURE__*/_jsxs(DialogHeader, {
          children: [/*#__PURE__*/_jsx(DialogCloseButton, {
            onClick: () => setHomemadeIframeModalOpen(false)
          }), /*#__PURE__*/_jsx(H2, {
            "aria-level": 2,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.cta.install.APP"
            })
          })]
        }), /*#__PURE__*/_jsx(DialogBody, {
          paddingBlockEnd: "400",
          children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectModal, {
            portalId: PortalIdParser.get(),
            appId: sourceId !== null && sourceId !== void 0 ? sourceId : listingId,
            sourceAppId: "app-marketplace",
            text: I18n.text('detailsPage.productDetailsCard.cta.install.APP'),
            onConnectionComplete: handleConnectionComplete,
            onIntegrationNotFound: () => {
              console.log('Integration not found');
            }
          })
        })]
      })]
    });
  }

  // Default integrations connect button for builtByHubSpot iframe
  if (installType === 'HOMEMADE_IFRAME') {
    return /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectButton, {
        portalId: PortalIdParser.get(),
        appId: sourceId !== null && sourceId !== void 0 ? sourceId : 0,
        sourceAppId: "app-marketplace",
        text: I18n.text('detailsPage.productDetailsCard.cta.install.APP'),
        onClick: onInstallClickDefault,
        onConnectionComplete: handleConnectionComplete,
        onConnectionFailed: handleConnectionFailed
      })
    });
  }

  // Fallback button
  return /*#__PURE__*/_jsx(Button, {
    use: "primary",
    disabled: true,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.install.APP"
    })
  });
}